import { type FC, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Callout, CalloutIntent, VStack } from "widgets";

import { createHiddenAttributesWarning, isAttributePartOfEditingGrid } from "../../../app-utils/sample.js";
import { pluralizeWord } from "../../../app-utils/string.js";
import { type Attribute } from "../../../graphql.js";
import { type IEditable } from "../../../redux/modules/data-editing.js";
import { uniquenessValidations } from "../../../redux/modules/sample-editing.js";
import { type IAppState } from "../../../redux/reducer.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";
import { useCountInvalidCells } from "../phenotype/EditingGrid.js";
import SampleEditingGrid from "./SampleEditingGrid.js";

type ISampleEditingGridScreenOwnProps = IPlaybookWizardStepProvidedProps;

/**
 * Screen with EditTable for modification of sample data
 */
const SampleEditingGridScreen: FC<ISampleEditingGridScreenOwnProps> = (
  props: ISampleEditingGridScreenOwnProps & IPlaybookWizardStepInjectedProps,
) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const [validating, setValidating] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(null);

  const rowsCount = useSelector<IAppState, number>((state) => state.sampleEditingState.editingGrid.length);
  const dataGrid = useSelector<IAppState, IEditable[]>((state) => state.sampleEditingState.editingGrid);
  const allAttributes = useSelector<IAppState, Attribute[]>((state) => state.sampleEditingState.sampleAttributes);

  const proceedWithNextStep = () => {
    setErrorMessage(null);
    setValidating(true);
    return dispatch(uniquenessValidations())
      .then(() => setValidating(false))
      .then(props.proceedWithNextStep)
      .catch((error: Error) => {
        setErrorMessage(error.message);
        setValidating(false);
      });
  };

  const invalidCellsCount: number = useCountInvalidCells(dataGrid);
  const multiValueWarning: string = createHiddenAttributesWarning(allAttributes);
  const attributes = useMemo(() => allAttributes.filter(isAttributePartOfEditingGrid), [allAttributes]);
  return (
    <PlaybookWizardStep
      {...props}
      wizardFooterData={{
        error:
          invalidCellsCount > 0
            ? `${invalidCellsCount} ${pluralizeWord("cell", invalidCellsCount)} ${pluralizeWord(
                "is",
                invalidCellsCount,
              )} invalid.`
            : errorMessage,
        nextButtonProps: {
          label: "Next",
          isDisabled: invalidCellsCount > 0 || rowsCount === 0,
          isLoading: validating,
          onPress: proceedWithNextStep,
        },
      }}
    >
      <VStack style={{ width: "100%", height: "100%" }}>
        {multiValueWarning && (
          <Callout intent={CalloutIntent.Warning}>Sample editing grid: {multiValueWarning}</Callout>
        )}

        <SampleEditingGrid
          attributes={attributes}
          templatesLookup={undefined} // the templates lookup does not apply there, since the read-only situations happens on row level
        />
      </VStack>
    </PlaybookWizardStep>
  );
};

export default SampleEditingGridScreen;
